import { Grid, Paper, Chip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ClassicDocumentIcon from 'app/assets/images/IconImages/ClassicDocumentIcon.svg';
import { convertToFormat } from 'app/helpers/dateHelper';
import { useTranslation } from 'react-i18next';
import {
  DocStatus,
  IPlacementClientConfirmationDoc,
  PlacementDocumentSource,
} from 'store/redux-store/placement-documents/types';
import { colors } from 'styles/styleVariables';
import { useDebouncedClick } from 'utils/hooks';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { CustomTooltip, OverflowTooltip } from '@AMIEWEB/Common';

const usePacketStyles = makeStyles()(theme => ({
  packetCard: {
    height: 116,
    border: `1px solid ${theme.palette.system.borderColor}`,
    padding: 20,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    boxShadow: 'none',
  },
  packetCardFocused: {
    border: `2px solid ${colors.amnBlue}`,
    backgroundColor: theme.palette.system.cardBackground,
    boxShadow: '0px 2px 5px #0000001A',
  },
  chip: {
    height: 27,
    paddingTop: 2,
  },
 
  version: {
    fontWeight: 450,
    fontSize: 16,
  },
  selected: {
    color: colors.amnBlue,
  },
  hovarableLink: {
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  icon: {
    color: colors.darkText,
  },
  templateName: {
    maxWidth: 115,
    fontSize: 12,
    color: theme.palette.system.neutralGray,
  },
  keyValue: {
    fontWeight: 450,
    whiteSpace: 'pre',
  },
}));

interface ConfirmationCardProps {
  docCard: IPlacementClientConfirmationDoc;
  selected?: boolean;
  onClick?: (docCard: IPlacementClientConfirmationDoc) => void;
  onDelete?: () => void;
  inlineDownload?: (docCard: IPlacementClientConfirmationDoc) => void;
}

export const ConfirmationCard = ({
  docCard,
  selected,
  onClick,
  onDelete,
  inlineDownload,
  ...props
}: ConfirmationCardProps) => {
  const { classes, cx } = usePacketStyles();
  const { t } = useTranslation();

  const [displayDeleteIcon, setDisplayDeleteIcon] = React.useState(false);

  //use custom hook to detect double click
  const _onClick = useDebouncedClick(event => {
    onClick?.(docCard);
  });

  const { isCreatedFromClassic, isDraft, isSent, hasSentDate, disableDownload, ifClassicDocHasNotBeenSent } =
    React.useMemo(() => {
      const classicDocIsNotSent =
        docCard.source === PlacementDocumentSource.classic ? docCard.status !== 'Sent' : false;
      return {
        isDraft: docCard.status === DocStatus.Draft,
        isSent: docCard.status === DocStatus.Sent,
        isCreatedFromClassic: docCard.source === PlacementDocumentSource.classic,
        hasSentDate: !!docCard.sentDate,
        disableDownload: !selected
          ? true
          : docCard.source === PlacementDocumentSource.classic
            ? docCard.status !== 'Sent'
            : false,
        ifClassicDocHasNotBeenSent: classicDocIsNotSent,
      };
    }, [docCard.sentDate, docCard.source, docCard.status, selected]);

  const handleDeleteIconHover = (state: boolean) => {
    if (isDraft) setDisplayDeleteIcon(state);
  };

  const handleDelete = event => {
    event.stopPropagation();
    event.preventDefault();
    onDelete();
  };

  const handleDownload = React.useCallback(
    (doublClick = false) => {
      if (disableDownload && (doublClick ? ifClassicDocHasNotBeenSent : true)) return;
      inlineDownload?.(docCard);
    },
    [disableDownload, ifClassicDocHasNotBeenSent, inlineDownload, docCard],
  );

  const _inlineDownload = useDebouncedClick(event => {
    event.stopPropagation();
    event.preventDefault();
    handleDownload();
  });

  const _inlineDoubleClickDownload = event => {
    event.stopPropagation();
    event.preventDefault();
    handleDownload(true);
    if (!selected) {
      onClick?.(docCard);
    }
  };

  return (
    <Paper
      className={cx(classes.packetCard, { [classes.packetCardFocused]: selected })}
      elevation={2}
      onClick={_onClick}
      onDoubleClick={_inlineDoubleClickDownload}
      onMouseEnter={() => handleDeleteIconHover(true)}
      onMouseLeave={() => handleDeleteIconHover(false)}
      sx={{ position: 'relative' }}
    >
      <DeleteOutlinedIcon
        sx={{ position: 'absolute', top: 0, right: 0, visibility: displayDeleteIcon ? 'visible' : 'hidden' }}
        classes={{ root: cx(classes.icon, classes.selected) }}
        onClick={handleDelete}
      />
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Grid container direction="row">
            <Grid item xs={1} sx={{ paddingTop: isCreatedFromClassic ? undefined : '2px' }}>
              {isCreatedFromClassic ? (
                <CustomTooltip tooltipContent="Generated in AMIE Classic">
                  <img src={ClassicDocumentIcon} alt="ClassicDocumentIcon" />
                </CustomTooltip>
              ) : (
                <DescriptionOutlinedIcon classes={{ root: cx(classes.icon, { [classes.selected]: selected }) }} />
              )}
            </Grid>
            <Grid item xs={7} sx={{ p: '6px 0 0 12px' }}>
              <Typography
                classes={{
                  root: cx(classes.version, {
                    [classes.selected]: !disableDownload,
                    [classes.hovarableLink]: !disableDownload,
                  }),
                }}
                onClick={_inlineDownload}
              >
                {isCreatedFromClassic
                  ? docCard.version
                  : t('placement.profile.submitPacket.dialog.content.existingPacket.version', {
                      version: docCard.version,
                    })}
              </Typography>
            </Grid>
            <Grid item xs={4} display="flex" justifyContent="flex-end">
              <Chip variant={isSent ? 'approved' : 'canceled'} className={classes.chip} label={docCard.status} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ marginLeft: '34px' }}>
          <Grid container>
            {isCreatedFromClassic ? null : (
              <Grid item xs={12}>
                <Typography variant="body2" classes={{ root: classes.keyValue }}>
                  {t('placement.profile.submitPacket.dialog.content.existingPacket.dateCreated', {
                    date: convertToFormat(docCard.createdOn, 'MM/DD/YYYY', true),
                  })}
                </Typography>
              </Grid>   
            )}
            {hasSentDate ? (
              <Grid item xs={12}>
                <Typography variant="body2" classes={{ root: classes.keyValue }}>
                  {t('placement.profile.submitPacket.dialog.content.existingPacket.sent', {
                    date: convertToFormat(docCard.sentDate, 'MM/DD/YYYY', true),
                  })}
                </Typography>
              </Grid>
            ) : null}
            {isCreatedFromClassic ? (
              <Grid item xs={12}>
                <Typography variant="body2" classes={{ root: classes.keyValue }}>
                  {t('placement.profile.submitPacket.dialog.content.existingPacket.lastUpdated', {
                    date: convertToFormat(docCard.updatedOn, 'MM/DD/YYYY', true),
                  })}
                </Typography>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <OverflowTooltip value={docCard.templateName} contentClass={classes.templateName} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};
